import { useTranslation } from 'react-i18next';

import OutlineButton from '@/components/ui/custom/buttons/OutlineButton';
import { ScheduledLessonFilters } from '@/data/services/scheduledLessonsServices';

import { OptionItem } from '@/models/OptionItem';
import { LessonTimeframeEnum } from '@/models/ScheduledLesson';

type TodayLessonsHeaderProps = {
  filter?: ScheduledLessonFilters;
  onChange(filter: ScheduledLessonFilters): void;
};

export default function TodayLessonsHeader({
  filter = {},
  onChange,
}: TodayLessonsHeaderProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'timeframeFilter',
  });

  const options: {
    [key in LessonTimeframeEnum]: OptionItem;
  } = {
    ALL: {
      id: LessonTimeframeEnum.ALL,
      text: t(LessonTimeframeEnum.ALL),
      active: filter.lessonTimeframe?.length === 0 || !filter?.lessonTimeframe,
      onClick: () => onChange({ ...filter, lessonTimeframe: undefined }),
    },
    ONGOING: {
      id: LessonTimeframeEnum.ONGOING,
      text: t(LessonTimeframeEnum.ONGOING),
      active:
        filter.lessonTimeframe &&
        filter.lessonTimeframe.includes(LessonTimeframeEnum.ONGOING),
    },
    PAST: {
      id: LessonTimeframeEnum.PAST,
      text: t(LessonTimeframeEnum.PAST),
      active:
        !!filter.lessonTimeframe &&
        filter.lessonTimeframe?.includes(LessonTimeframeEnum.PAST),
    },
    UPCOMING: {
      id: LessonTimeframeEnum.UPCOMING,
      text: t(LessonTimeframeEnum.UPCOMING),
      active:
        !!filter.lessonTimeframe &&
        filter.lessonTimeframe?.includes(LessonTimeframeEnum.UPCOMING),
    },
  };

  function handleClick(value: string) {
    const active = filter.lessonTimeframe?.some(v => v === value);

    if (active) {
      onChange({
        ...filter,
        lessonTimeframe: filter.lessonTimeframe?.filter(v => v !== value),
      });
    } else {
      const values = filter.lessonTimeframe ?? [];
      onChange({
        ...filter,
        lessonTimeframe: [...values, value as LessonTimeframeEnum],
      });
    }
  }

  return (
    <ul className="flex gap-1.5 flex-wrap">
      {Object.values(options).map(option => (
        <li key={option.id}>
          <OutlineButton
            aria-pressed={option.active}
            testId={option.id}
            text={option.text}
            className={{
              base: 'lowercase text-[0.75rem] sm:text-[0.875rem] px-1.5 py-0',
            }}
            onClick={
              option.onClick ? option.onClick : () => handleClick(option.id)
            }
            active={option.active}
          />
        </li>
      ))}
    </ul>
  );
}
